import { useState, useMemo } from 'react'
import {
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  flexRender,
  CellContext,
  ColumnResizeMode
} from '@tanstack/react-table'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '../ui/Table'
import Button from '../ui/Button'

function EditableCell(props: CellContext<Record<string, string>, unknown>) {
  const { cell, row, table } = props
  const prevValue = cell.getValue() as string
  const { updateData } = table.options.meta as unknown as {
    updateData: (
      rowIndex: number,
      columnId: string,
      value: string | number | null | undefined
    ) => void
  }
  const value = cell.getValue() as string
  const [isEditing, setIsEditing] = useState<boolean>(false)

  const onBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsEditing(false)
    updateData(row.index, cell.column.id, event.target.value)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      onBlur(event as unknown as React.FocusEvent<HTMLTextAreaElement>)
    }

    if (event.key === 'Escape') {
      updateData(row.index, cell.column.id, prevValue)
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <textarea
        data-cell-id={cell.id}
        defaultValue={value}
        autoFocus
        onFocus={(event) => event.currentTarget.select()}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={`outline-none w-full h-full border ${
          isEditing ? 'border-primary' : 'border-transparent'
        } px-2 py-1 resize-none whitespace-pre-wrap break-words`}
      />
    )
  }

  return (
    <div
      className="px-2 py-1 whitespace-pre-wrap break-words cursor-text w-full h-full min-h-8"
      onClick={() => setIsEditing(true)}
    >
      {value}
    </div>
  )
}

const getColumnsDef = (columns: string[], columnWidths: Record<string, number>) => {
  return columns.map((column) => {
    return {
      accessorKey: column,
      header: column,
      size: columnWidths[column] ?? 150,
      minSize: 50,
      maxSize: 800,
      cell: (props: CellContext<Record<string, string>, unknown>) => (
        <EditableCell {...props} />
      )
    }
  })
}

function computeColumnWidths(
  rows: Record<string, string>[],
  columns: string[]
): Record<string, number> {
  const MAX_WIDTH = 1200
  const MIN_WIDTH = 50
  const AVERAGE_CHAR_WIDTH_PX = 8
  const HORIZONTAL_PADDING_PX = 24

  const widths: Record<string, number> = {}

  for (const column of columns) {
    let maxChars = (column ?? '').length
    const sample = rows.slice(0, 200)
    for (const row of sample) {
      const value = row[column]
      const len = (value ?? '').toString().length
      if (len > maxChars) maxChars = len
    }
    const computed = maxChars * AVERAGE_CHAR_WIDTH_PX + HORIZONTAL_PADDING_PX
    widths[column] = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, computed))
  }

  return widths
}

export default function EditableTable({
  data,
  columns,
  setData
}: {
  data: Record<string, string>[]
  columns: string[]
  setData: (data: Record<string, string>[]) => void
}) {
  const columnWidths = useMemo(() => computeColumnWidths(data, columns), [data, columns])

  const columnsDef: ColumnDef<Record<string, string>>[] = useMemo(
    () => getColumnsDef(columns, columnWidths),
    [columns, columnWidths]
  )

  const table = useReactTable({
    data,
    columns: columnsDef,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange' as ColumnResizeMode,
    enableColumnResizing: true,
    meta: {
      updateData: (
        rowIndex: number,
        columnId: keyof Record<string, string>,
        value: string
      ) => {
        const nextTableData: Record<string, string>[] = data.map((item, index) => {
          if (rowIndex !== index) return item
          return {
            ...item,
            [columnId]: value
          }
        })
        setData(nextTableData)
      }
    }
  })

  const onClickAddRow = () => {
    const newRow = Object.fromEntries(columns.map((col) => [col, '']))
    setData([
      ...data,
      {
        ID: window.crypto.randomUUID(),
        ...newRow
      }
    ])
  }

  return (
    <div className="border border-divider">
      <Table className="w-auto min-w-[1270px] bg-white">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="border-r bg-gray-200 font-semibold relative whitespace-pre-wrap break-words"
                  style={{ width: header.getSize(), maxWidth: 1200 }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  <div
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none ${
                      header.column.getIsResizing() ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            return (
              <TableRow key={row.id} className="p-0">
                {row.getVisibleCells().map((cell) => {
                  return (
                    <TableCell
                      key={cell.id}
                      className="border-r p-0 whitespace-pre-wrap break-words align-top"
                      style={{
                        width: cell.column.getSize(),
                        maxWidth: 800
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
          <TableRow>
            <TableCell colSpan={columns.length} className="p-0">
              <Button
                variant="secondary"
                className="w-full rounded-none h-7"
                onClick={onClickAddRow}
              >
                + Add Row
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
